'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyBottomBar from '@/components/StickyBottomBar';
import {
  Users,
  TrendingUp,
  FlaskConical,
  BarChart3,
  Layers,
  Bell,
  MonitorPlay,
  PanelBottom,
  CheckCircle,
  ArrowRight,
  Mail,
  Building2,
  DollarSign,
  MessageSquare,
  ChevronRight } from
'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface InquiryForm {
  name: string;
  company: string;
  email: string;
  budget: string;
  message: string;
}

const stats = [
{ icon: DollarSign, value: '$2.1B+', label: 'U.S. Peptide Market', sub: 'Total addressable market tracked' },
{ icon: Users, value: '200K+', label: 'Industry Subscribers', sub: 'Suppliers, researchers, compliance officers' },
{ icon: FlaskConical, value: '340+', label: 'Compounds Tracked', sub: 'Across all research categories' },
{ icon: BarChart3, value: '61%', label: 'COA-Verified Suppliers', sub: 'Quality-focused B2B audience' }];


const placements = [
{
  id: 'placement-native',
  icon: Layers,
  name: 'Native Article Card',
  description: 'Sponsored content card placed within the article feed and homepage story grid. Matches editorial card design with SPONSORED label. Highest engagement format.',
  specs: 'Headline (max 80 chars) · Subtext (max 140 chars) · CTA link · Optional image',
  cpm: '$45–$65 CPM',
  tag: 'Most Popular',
  tagColor: 'bg-primary text-white'
},
{
  id: 'placement-interstitial',
  icon: MonitorPlay,
  name: 'Interstitial Modal',
  description: 'Full-screen overlay shown once per session to all site visitors. Highest visibility format. Ideal for product launches, report promotions, and event registrations.',
  specs: 'Headline · Body copy · Email capture or CTA button · Logo',
  cpm: '$80–$120 CPM',
  tag: 'Premium',
  tagColor: 'bg-foreground text-white'
},
{
  id: 'placement-toast',
  icon: Bell,
  name: 'Toast Notification',
  description: 'Bottom-left dismissible notification displayed to all active users. Ideal for breaking news sponsorships, time-sensitive announcements, and event promotions.',
  specs: 'Short message (max 100 chars) · Optional link text · Auto-dismiss after 8s',
  cpm: '$25–$40 CPM',
  tag: 'High Frequency',
  tagColor: 'bg-blue-600 text-white'
},
{
  id: 'placement-sticky',
  icon: PanelBottom,
  name: 'Sticky Bottom Bar',
  description: 'Persistent bottom bar visible across all pages until dismissed. Continuous brand presence throughout the user session. Strong for brand awareness campaigns.',
  specs: 'Short headline · CTA button · Dismiss button included',
  cpm: '$30–$50 CPM',
  tag: 'Brand Awareness',
  tagColor: 'bg-secondary-foreground text-white'
}];


const audienceBreakdown = [
{ segment: '503B Outsourcing Facility Staff', pct: 34 },
{ segment: 'Peptide API Suppliers & Manufacturers', pct: 28 },
{ segment: 'Compounding Pharmacists & Directors', pct: 18 },
{ segment: 'Regulatory & Compliance Counsel', pct: 12 },
{ segment: 'Academic & Clinical Researchers', pct: 8 }];


const budgetOptions = [
'Select budget range',
'Under $2,500 / month',
'$2,500–$5,000 / month',
'$5,000–$10,000 / month',
'$10,000–$25,000 / month',
'$25,000+ / month',
'Custom / Annual package'];


export default function AdvertiseContent() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<InquiryForm>();

  function onSubmit(_data: InquiryForm) {
    setSubmitting(true);
    // Backend integration point: POST /api/advertise-inquiry with form data
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1000);
  }

  return (
    <>
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <div className="relative h-[340px] lg:h-[420px] overflow-hidden">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_14a229231-1786579931112.png"
            alt="Professional pharmaceutical industry conference representing the USA Peptide Pros advertiser audience"
            fill
            priority
            sizes="100vw"
            className="object-cover" />
          
          <div className="absolute inset-0 hero-gradient" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pb-10 w-full">
              <span className="section-eyebrow text-white/80 mb-2 block">Media Kit</span>
              <h1 className="text-hero-md font-800 text-white leading-tight">
                Reach the Decision-Makers <br className="hidden lg:block" />
                in U.S. Peptide Supply
              </h1>
              <p className="mt-3 text-white/80 text-base max-w-xl">
                The only independent intelligence platform covering the full U.S. peptide industry — regulatory, supply chain, testing, and market data.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          {/* Breadcrumb */}
          <nav className="py-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-foreground">Advertise</span>
          </nav>

          {/* Stats row */}
          <section className="py-8 border-b border-border">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={`adstat-${stat.label}`} className="bg-secondary border border-border rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon size={16} className="text-primary" />
                      </div>
                    </div>
                    <p className="text-2xl lg:text-3xl font-800 text-foreground tabular-nums">{stat.value}</p>
                    <p className="text-sm font-600 text-foreground mt-0.5">{stat.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stat.sub}</p>
                  </div>);

              })}
            </div>
          </section>

          {/* Audience breakdown */}
          <section className="py-10 border-b border-border">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="section-eyebrow mb-2 block">Who You Reach</span>
                <h2 className="text-2xl font-700 text-foreground mb-3">A High-Intent B2B Audience</h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-6">
                  Our readers are procurement directors, compliance officers, and supply chain managers who make sourcing and vendor decisions daily. This is not a consumer audience — every subscriber has a professional reason to be here.
                </p>
                <div className="space-y-3">
                  {audienceBreakdown.map((seg) =>
                  <div key={`seg-${seg.segment}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-500 text-foreground">{seg.segment}</span>
                        <span className="text-sm font-700 text-primary tabular-nums">{seg.pct}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${seg.pct}%` }} />
                      
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                { label: 'Avg. Time on Site', value: '4m 32s', icon: TrendingUp },
                { label: 'Weekly Active Readers', value: '47K+', icon: Users },
                { label: 'Newsletter Open Rate', value: '38.4%', icon: Mail },
                { label: 'Pages per Session', value: '3.8', icon: BarChart3 }].
                map((m) => {
                  const Icon = m.icon;
                  return (
                    <div key={`metric-${m.label}`} className="bg-card border border-border rounded-lg p-4 text-center">
                      <Icon size={20} className="text-primary mx-auto mb-2" />
                      <p className="text-xl font-800 text-foreground tabular-nums">{m.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
                    </div>);

                })}
              </div>
            </div>
          </section>

          {/* Ad placements */}
          <section className="py-10 border-b border-border">
            <span className="section-eyebrow mb-2 block">Ad Placements</span>
            <h2 className="text-2xl font-700 text-foreground mb-2">Available Formats</h2>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl">
              All placements include editorial team review to ensure alignment with our audience and content standards. Sponsored content is always clearly labeled.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {placements.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.id} className="bg-card border border-border rounded-lg p-6 card-hover">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon size={20} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="text-base font-700 text-foreground">{p.name}</h3>
                          <p className="text-sm font-700 text-primary tabular-nums">{p.cpm}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-700 px-2.5 py-1 rounded-full ${p.tagColor} flex-shrink-0`}>
                        {p.tag}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">{p.description}</p>
                    <div className="p-3 rounded-md bg-secondary border border-border">
                      <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-1">Specs</p>
                      <p className="text-xs text-foreground">{p.specs}</p>
                    </div>

                    {/* Visual mockup hint */}
                    <div className="mt-4 h-20 rounded-md bg-muted border border-border flex items-center justify-center">
                      <span className="text-xs text-muted-foreground font-600 uppercase tracking-wide">
                        [{p.name} Preview]
                      </span>
                    </div>
                  </div>);

              })}
            </div>

            <div className="mt-6 p-5 rounded-lg bg-secondary border border-border">
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-verified flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-700 text-foreground mb-1">Editorial Separation Policy</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    All sponsored content is clearly labeled as SPONSORED or ADVERTISEMENT. Advertiser relationships do not influence editorial coverage. See our{' '}
                    <Link href="/editorial-standards" className="text-primary hover:underline font-600">
                      Editorial Standards
                    </Link>{' '}
                    for full disclosure policy.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Inquiry form */}
          <section className="py-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <span className="section-eyebrow mb-2 block">Get Started</span>
                <h2 className="text-2xl font-700 text-foreground mb-3">Request Our Media Kit</h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-6">
                  Fill out the form and our partnerships team will respond within one business day with a full media kit, rate card, and available inventory.
                </p>
                <div className="space-y-4">
                  {[
                  { icon: Users, text: '200K+ verified industry professional subscribers' },
                  { icon: TrendingUp, text: 'Fastest-growing peptide industry publication in the U.S.' },
                  { icon: Building2, text: 'Audience spans suppliers, 503B facilities, and research institutions' },
                  { icon: CheckCircle, text: 'All placements reviewed for content alignment' }].
                  map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={`benefit-${item.text.slice(0, 20)}`} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon size={15} className="text-primary" />
                        </div>
                        <p className="text-sm text-foreground leading-relaxed pt-1">{item.text}</p>
                      </div>);

                  })}
                </div>
              </div>

              {/* Form */}
              <div className="bg-card border border-border rounded-xl p-6 lg:p-8">
                {submitted ?
                <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={28} className="text-green-600" />
                    </div>
                    <h3 className="text-xl font-700 text-foreground mb-2">Inquiry Received</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Our partnerships team will reach out within one business day with media kit and available inventory.
                    </p>
                    <Link href="/" className="btn-primary inline-flex">
                      Return to Homepage <ArrowRight size={16} />
                    </Link>
                  </div> :

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                    <h3 className="text-lg font-700 text-foreground mb-1">Advertiser Inquiry</h3>

                    {/* Name */}
                    <div>
                      <label htmlFor="adv-name" className="block text-sm font-600 text-foreground mb-1">
                        Full Name <span className="text-primary">*</span>
                      </label>
                      <input
                      id="adv-name"
                      type="text"
                      placeholder="Sarah Chen"
                      className="input-base"
                      {...register('name', { required: 'Full name is required' })} />
                    
                      {errors.name &&
                    <p className="mt-1 text-xs text-primary font-500">{errors.name.message}</p>
                    }
                    </div>

                    {/* Company */}
                    <div>
                      <label htmlFor="adv-company" className="block text-sm font-600 text-foreground mb-1">
                        Company / Organization <span className="text-primary">*</span>
                      </label>
                      <input
                      id="adv-company"
                      type="text"
                      placeholder="PurityLabs Inc."
                      className="input-base"
                      {...register('company', { required: 'Company name is required' })} />
                    
                      {errors.company &&
                    <p className="mt-1 text-xs text-primary font-500">{errors.company.message}</p>
                    }
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="adv-email" className="block text-sm font-600 text-foreground mb-1">
                        Work Email <span className="text-primary">*</span>
                      </label>
                      <input
                      id="adv-email"
                      type="email"
                      placeholder="s.chen@puritylabs.com"
                      className="input-base"
                      {...register('email', {
                        required: 'Email address is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Enter a valid email address'
                        }
                      })} />
                    
                      {errors.email &&
                    <p className="mt-1 text-xs text-primary font-500">{errors.email.message}</p>
                    }
                    </div>

                    {/* Budget */}
                    <div>
                      <label htmlFor="adv-budget" className="block text-sm font-600 text-foreground mb-1">
                        Monthly Budget Range <span className="text-primary">*</span>
                      </label>
                      <div className="relative">
                        <select
                        id="adv-budget"
                        className="select-base pr-8"
                        {...register('budget', {
                          required: 'Please select a budget range',
                          validate: (v) => v !== 'Select budget range' || 'Please select a budget range'
                        })}>
                        
                          {budgetOptions.map((opt) =>
                        <option key={`budget-${opt}`} value={opt}>{opt}</option>
                        )}
                        </select>
                      </div>
                      {errors.budget &&
                    <p className="mt-1 text-xs text-primary font-500">{errors.budget.message}</p>
                    }
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="adv-message" className="block text-sm font-600 text-foreground mb-1">
                        Campaign Goals / Message
                      </label>
                      <p className="text-xs text-muted-foreground mb-1">
                        Tell us about your target audience, campaign objectives, or any specific placements you&apos;re interested in.
                      </p>
                      <textarea
                      id="adv-message"
                      rows={4}
                      placeholder="We're looking to reach 503B facility procurement directors with our new COA verification platform..."
                      className="input-base resize-none"
                      {...register('message')} />
                    
                    </div>

                    <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full justify-center py-3 mt-2">
                    
                      {submitting ?
                    <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Submitting Inquiry...
                        </span> :

                    <span className="flex items-center gap-2">
                          <MessageSquare size={16} /> Submit Inquiry
                        </span>
                    }
                    </button>

                    <p className="text-xs text-muted-foreground text-center">
                      Required fields marked with <span className="text-primary font-700">*</span>. We respond within 1 business day.
                    </p>
                  </form>
                }
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <StickyBottomBar />
    </>);

}